import React, { useEffect, useMemo, useRef, useState } from "react";
import { getApiBase, apiUrl } from "../lib/apiBase";

function getToken() {
  try {
    return (
      localStorage.getItem("ga_token") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("token") ||
      localStorage.getItem("jwt") ||
      ""
    );
  } catch {
    return "";
  }
}

function toAssetUrl(value = "") {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^(?:https?:|data:|blob:)/i.test(raw)) return raw;
  const base = getApiBase().replace(/\/+$/, "");
  return `${base}${raw.startsWith("/") ? "" : "/"}${raw}`;
}

const THEMES = [
  { key: "gas", label: "GAS" },
  { key: "midnight", label: "Midnight" },
  { key: "neon", label: "Neon" },
  { key: "royal", label: "Royal" },
  { key: "classic", label: "Classic" },
];

export default function PublicProfileCustomizer({ user }) {
  const fileRef = useRef(null);
  const [theme, setTheme] = useState("gas");
  const [accent, setAccent] = useState("#e10613");
  const [mode, setMode] = useState("cover");
  const [backgroundUrl, setBackgroundUrl] = useState("");
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");

  const username = String(user?.username || user?.name || "").trim();

  useEffect(() => {
    setTheme(String(user?.profileTheme || "gas"));
    setAccent(String(user?.profileAccentColor || "#e10613"));
    setMode(String(user?.profileBackgroundMode || "cover"));
    setBackgroundUrl(String(user?.profileBackgroundUrl || ""));
  }, [
    user?.profileTheme,
    user?.profileAccentColor,
    user?.profileBackgroundMode,
    user?.profileBackgroundUrl,
  ]);

  useEffect(() => {
    if (window.location.hash !== "#public-profile-customizer") return undefined;
    const timer = window.setTimeout(() => {
      document.getElementById("public-profile-customizer")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 180);
    return () => window.clearTimeout(timer);
  }, []);

  const previewBackground = useMemo(() => toAssetUrl(backgroundUrl), [backgroundUrl]);

  async function saveSettings() {
    const token = getToken();
    if (!token || busy) return;
    setBusy("save");
    setMessage("");
    try {
      const response = await fetch(apiUrl("/public-profiles/me/customization"), {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          profileTheme: theme,
          profileAccentColor: accent,
          profileBackgroundMode: mode,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.ok) {
        throw new Error(data?.message || data?.error || "Could not save profile style.");
      }
      setTheme(String(data?.profile?.profileTheme || theme));
      setAccent(String(data?.profile?.profileAccentColor || accent));
      setMode(String(data?.profile?.profileBackgroundMode || mode));
      setMessage("Public profile style saved.");
    } catch (error) {
      setMessage(error?.message || "Could not save profile style.");
    } finally {
      setBusy("");
    }
  }

  async function uploadBackground(file) {
    if (!file || busy) return;
    const token = getToken();
    if (!token) return;

    if (!String(file.type || "").toLowerCase().startsWith("image/")) {
      setMessage("Choose an image file.");
      return;
    }
    if (Number(file.size || 0) > 8 * 1024 * 1024) {
      setMessage("Background images must be 8 MB or smaller.");
      return;
    }

    setBusy("background");
    setMessage("");
    try {
      const form = new FormData();
      form.append("background", file);
      const response = await fetch(apiUrl("/public-profiles/me/background"), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.ok) {
        throw new Error(
          data?.error === "profile_background_too_large"
            ? "Background images must be 8 MB or smaller."
            : data?.message || data?.error || "Could not upload background."
        );
      }
      setBackgroundUrl(String(data?.backgroundUrl || data?.profile?.profileBackgroundUrl || ""));
      setMessage("Background uploaded. Your public profile is updated.");
    } catch (error) {
      setMessage(error?.message || "Could not upload background.");
    } finally {
      setBusy("");
    }
  }

  async function removeBackground() {
    if (!backgroundUrl || busy) return;
    const token = getToken();
    if (!token) return;
    setBusy("remove");
    setMessage("");
    try {
      const response = await fetch(apiUrl("/public-profiles/me/background"), {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.ok) {
        throw new Error(data?.message || data?.error || "Could not remove background.");
      }
      setBackgroundUrl("");
      setMessage("Background removed.");
    } catch (error) {
      setMessage(error?.message || "Could not remove background.");
    } finally {
      setBusy("");
    }
  }

  async function copyProfileLink() {
    if (!username) return;
    const url = `${window.location.origin}/u/${encodeURIComponent(username)}`;
    try {
      await navigator.clipboard.writeText(url);
      setMessage("Profile link copied.");
    } catch {
      setMessage(url);
    }
  }

  const panel = {
    borderRadius: 20,
    padding: 18,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(0,0,0,0.32)",
  };

  const button = {
    minHeight: 40,
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.15)",
    background: "rgba(255,255,255,0.06)",
    color: "white",
    padding: "0 14px",
    fontWeight: 900,
    cursor: busy ? "wait" : "pointer",
  };

  return (
    <section id="public-profile-customizer" style={{ ...panel, marginTop: 22, scrollMarginTop: 170 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={{ fontSize: 18, fontWeight: 1000 }}>Public Profile Customizer</div>
          <div style={{ marginTop: 5, opacity: 0.72, fontSize: 13, lineHeight: 1.5 }}>
            Trial version: pick a theme, accent color, and your own background. The public layout stays
            mobile-safe so a desktop background cannot break the Android or iPhone view.
          </div>
        </div>
        {username ? (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button style={button} type="button" onClick={() => window.location.assign(`/u/${encodeURIComponent(username)}`)}>
              View public profile
            </button>
            <button style={button} type="button" onClick={copyProfileLink}>
              Copy link
            </button>
          </div>
        ) : null}
      </div>

      <div
        style={{
          marginTop: 16,
          minHeight: 170,
          borderRadius: 18,
          overflow: "hidden",
          border: `1px solid ${accent}88`,
          position: "relative",
          background: "#08090d",
        }}
      >
        {previewBackground ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url("${previewBackground}")`,
              backgroundSize: mode === "tile" ? "auto" : "cover",
              backgroundRepeat: mode === "tile" ? "repeat" : "no-repeat",
              backgroundPosition: mode === "tile" ? "top left" : "center",
            }}
          />
        ) : null}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(180deg, rgba(0,0,0,.42), rgba(0,0,0,.86)), radial-gradient(520px 220px at 10% 0%, ${accent}55, transparent 70%)`,
          }}
        />
        <div style={{ position: "relative", zIndex: 2, padding: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 950, letterSpacing: 1.1, opacity: 0.75 }}>
            PROFILE PREVIEW
          </div>
          <div style={{ fontSize: 27, fontWeight: 1000, marginTop: 5 }}>
            {user?.displayName || username || "Your profile"}
          </div>
          <div style={{ color: accent, fontWeight: 950, marginTop: 3 }}>@{username || "username"}</div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
          gap: 16,
          marginTop: 16,
        }}
      >
        <div>
          <div style={{ fontWeight: 950, marginBottom: 9 }}>Theme</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {THEMES.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setTheme(item.key)}
                style={{
                  ...button,
                  background: theme === item.key ? "rgba(225,6,19,.32)" : button.background,
                  borderColor: theme === item.key ? "rgba(255,85,105,.60)" : "rgba(255,255,255,.15)",
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div style={{ fontWeight: 950, marginBottom: 9 }}>Accent color</div>
          <div style={{ display: "flex", gap: 9, alignItems: "center" }}>
            <input
              type="color"
              value={/^#[0-9a-f]{6}$/i.test(accent) ? accent : "#e10613"}
              onChange={(event) => setAccent(event.target.value)}
              style={{ width: 52, height: 42, padding: 2, border: 0, background: "transparent" }}
              aria-label="Profile accent color"
            />
            <input
              value={accent}
              maxLength={7}
              onChange={(event) => setAccent(event.target.value)}
              style={{
                minHeight: 42,
                width: 112,
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,.14)",
                background: "rgba(0,0,0,.34)",
                color: "white",
                padding: "0 10px",
              }}
            />
          </div>
        </div>

        <div>
          <div style={{ fontWeight: 950, marginBottom: 9 }}>Background style</div>
          <select
            value={mode}
            onChange={(event) => setMode(event.target.value)}
            style={{
              minHeight: 42,
              width: "100%",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,.14)",
              background: "#111218",
              color: "white",
              padding: "0 10px",
              fontWeight: 800,
            }}
          >
            <option value="cover">Fill profile (best for phones)</option>
            <option value="tile">Tile it (old-school MySpace)</option>
          </select>
        </div>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp"
        style={{ display: "none" }}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) uploadBackground(file);
        }}
      />

      <div style={{ display: "flex", gap: 9, flexWrap: "wrap", marginTop: 16 }}>
        <button
          type="button"
          style={{ ...button, background: "linear-gradient(90deg, rgba(225,6,19,.86), rgba(128,0,20,.86))" }}
          onClick={saveSettings}
          disabled={Boolean(busy)}
        >
          {busy === "save" ? "Saving…" : "Save style"}
        </button>
        <button
          type="button"
          style={button}
          onClick={() => fileRef.current?.click()}
          disabled={Boolean(busy)}
        >
          {busy === "background" ? "Uploading…" : backgroundUrl ? "Change background" : "Add background"}
        </button>
        {backgroundUrl ? (
          <button type="button" style={button} onClick={removeBackground} disabled={Boolean(busy)}>
            {busy === "remove" ? "Removing…" : "Remove background"}
          </button>
        ) : null}
      </div>

      {message ? (
        <div
          role="status"
          style={{
            marginTop: 12,
            padding: "10px 12px",
            borderRadius: 12,
            border: "1px solid rgba(255,255,255,.10)",
            background: "rgba(255,255,255,.05)",
            fontSize: 13,
            lineHeight: 1.45,
          }}
        >
          {message}
        </div>
      ) : null}
    </section>
  );
}
